import type { CustomButtonEventPayload } from '@components/sp-custom-button';
import type {
    ChatBotWidgetData,
    ChatBotData,
    ChatBotWidgetDataFormType,
} from '@web/widgets/chat-bot/type/chat-bot-widget.type';
import type { EIconType, ESize } from '@common/enums';
import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import type { ShadowSettings } from '@common/types/shadow-settings.type';

import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { moveItemInArray, DragDropModule } from '@angular/cdk/drag-drop';
import type { Observable } from 'rxjs';

import { EHorizontalPosition, EButtonType, EButtonStyleType } from '@common/enums';
import { ERadioGroupType } from '@ui/sp-radio-group';
import { arrayLengthEq } from '@common/helpers';

import { WebBaseControlComponent } from '@web/widgets/common/abstract/web-base-control.component';
import { ICON_TYPES } from '@web/widgets/chat-bot/constants/chat-bot-widget.const';

import { getSocialNetworkIconSrc } from '@web/shared/components/web-social-networks/utils';

import { chatBotWidgetDataFormGroup, chatBotElementFormGroup } from '@web/widgets/chat-bot/form/chat-bot-widget.form';

import { ChatBotsCollectionFacade } from '@web/store/collections/chat-bots/chat-bots.facade';
import { EChatBotsMessengers } from '@common/enums/chat-bots.enum';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { WebCustomButtonComponent } from '@web/shared/components/web-custom-button/web-custom-button.component';
import { ChatBotSettingsComponent } from '@web/widgets/chat-bot/components/chat-bot-control/chat-bot-settings/chat-bot-settings.component';
import { WebSocialNetworksComponent } from '@web/shared/components/web-social-networks/web-social-networks.component';
import { WebWidgetExtendedHideSettingsComponent } from '@web/shared/components/web-widget-extended-hide-settings';
import { WebWidgetPositionSettingsComponent } from '@web/shared/components/web-widget-position-settings';
import { SpShadowSettingsComponent } from '@components/sp-shadow-settings/sp-shadow-settings.component';

@Component({
    selector: 'web-chat-bot-control',
    templateUrl: './chat-bot-control.component.html',
    styleUrls: ['./chat-bot-control.component.less'],
    imports: [
        CommonElementsModule,
        DragDropModule,
        WebCustomButtonComponent,
        WebSocialNetworksComponent,
        ChatBotSettingsComponent,
        WebWidgetExtendedHideSettingsComponent,
        WebWidgetPositionSettingsComponent,
        SpShadowSettingsComponent,
    ],
})
export class ChatBotControlComponent extends WebBaseControlComponent<ChatBotWidgetData> {
    private readonly chatBotsCollectionFacade: ChatBotsCollectionFacade = inject(ChatBotsCollectionFacade);

    public readonly ICON_SIZE_MAX: number = 200;

    public readonly EButtonType = EButtonType;
    public readonly EHorizontalPosition = EHorizontalPosition;
    public readonly ERadioGroupType = ERadioGroupType;
    public readonly EButtonStyleType = EButtonStyleType;

    public readonly ICON_TYPES = ICON_TYPES;

    private readonly EMPTY_LINK_TEXT = this.translateService.instant('provide_link');

    public editableChatBotIndex: number = null;
    public isIcon = false;

    protected initForm(): void {
        this.controlForm = this.fb.group({
            data: chatBotWidgetDataFormGroup(),
        });
    }

    protected setFormData(): void {
        if (this.chatBots.length !== this.widgetData.chatBots.length) {
            this.chatBots.clear({ emitEvent: false });
            const diff = this.widgetData.chatBots.length - this.chatBots.length;
            for (let i = 0; i < diff; i++) {
                this.chatBots.push(chatBotElementFormGroup(), { emitEvent: false });
            }
        }

        this.controlForm.patchValue(
            {
                data: { ...this.widgetData },
            },
            { emitEvent: false }
        );

        if (
            !this.widgetData?.advancedRadius?.borderTopLeftRadius &&
            this.widgetData?.advancedRadius?.borderTopLeftRadius !== 0
        ) {
            this.data.patchValue(
                {
                    isIndependentCorners: false,
                    advancedRadius: {
                        borderTopLeftRadius: +this.widgetData?.buttonBase?.borderRadius,
                        borderTopRightRadius: +this.widgetData?.buttonBase?.borderRadius,
                        borderBottomLeftRadius: +this.widgetData?.buttonBase?.borderRadius,
                        borderBottomRightRadius: +this.widgetData?.buttonBase?.borderRadius,
                    },
                },
                { emitEvent: false }
            );
        }

        if (!this.widgetData.mobileHorizontalAlign) {
            this.data.patchValue({ mobileHorizontalAlign: this.widgetData.horizontalAlign }, { emitEvent: false });
        }

        if (!this.widgetData.tabletHorizontalAlign) {
            this.data.patchValue({ tabletHorizontalAlign: this.widgetData.horizontalAlign }, { emitEvent: false });
        }

        if (this.isSynchronizeStyle.value === null) {
            this.controlForm.patchValue(
                {
                    data: {
                        isSynchronizeStyle: true,
                    },
                },
                { emitEvent: false }
            );
        }

        this.setElementType();
    }

    public addChatBot(): void {
        const chatBotForm = chatBotElementFormGroup();
        chatBotForm.patchValue(
            {
                socialNetwork: this.getUnselectedSocialNetwork() as any,
                type: this.type.value,
                iconType: this.iconType.value,
                name: this.getUnselectedSocialNetwork(),
            },
            { emitEvent: false }
        );

        this.chatBots.push(chatBotForm);
    }

    public editChatBot(index: number): void {
        this.editableChatBotIndex = index;
    }

    public get editingChatBot(): any {
        return this.chatBots.at(this.editableChatBotIndex);
    }

    public onSwitchButtonType(type: EButtonType) {
        const chatBots = this.chatBots.value.map((chatBot: ChatBotData) => ({
            ...chatBot,
            type,
        }));

        this.data.patchValue({
            chatBots,
            type,
        });
        this.setElementType();
    }

    public setIconType(iconType: EIconType): void {
        const chatBots = this.chatBots.value.map((chatBot: ChatBotData) => ({
            ...chatBot,
            iconType,
        }));

        this.data.patchValue({
            chatBots,
            iconType,
        });
    }

    private setElementType(): void {
        this.isIcon = this.type.value === this.EButtonType.icon;
    }

    public customButtonChangeHandler(changes: CustomButtonEventPayload): void {
        this.buttonBase.setValue(changes.base as any, { emitEvent: false });
        // `as any` for the same reason as the line above: the form group declares every hover field
        // required, while the schema-inferred `ThemeButtonHover` reads as all-optional under this
        // workspace's non-strict `strictNullChecks`.
        this.buttonHover.setValue(changes.hover as any);

        if (changes?.isIndependentCorners !== undefined) {
            this.isIndependentCorners.setValue(changes.isIndependentCorners);
        }
        if (changes?.radiusSettings) {
            this.advancedRadius.setValue(changes.radiusSettings);
        }
        if (changes?.advancedBorderSettings) {
            this.advancedBorderSettings.setValue(changes.advancedBorderSettings);
        }
    }

    public removeSocial(i: number) {
        if (arrayLengthEq(this.chatBots.value, 1)) {
            return;
        }

        this.chatBots.removeAt(i);
    }

    private getUnselectedSocialNetwork(): EChatBotsMessengers {
        const difference = this.getMessengerNames().filter(
            (type) => !this.chatBots.value.map((el) => el.socialNetwork).includes(type as any)
        );

        return difference.length > 0 ? difference[0] : this.getMessengerNames()[0];
    }

    public getMessengerNames(): EChatBotsMessengers[] {
        return Object.keys(EChatBotsMessengers).filter(
            (key) => key !== EChatBotsMessengers.vk && key !== EChatBotsMessengers.ok
        ) as EChatBotsMessengers[];
    }

    public getSocialNetworkIconSrcByChatBot(chatBot: ChatBotData): string {
        return getSocialNetworkIconSrc(chatBot.iconType, chatBot.socialNetwork);
    }

    public getChatBotSubText(chatBot: ChatBotData): string {
        return chatBot.link ? chatBot.link : this.EMPTY_LINK_TEXT;
    }

    public setHeight(value: number): void {
        this.height.setValue(value);
    }

    public setHorizontalAlign(value: string): void {
        if (this.isSynchronizeStyle.value) {
            this.mobileHorizontalAlign.setValue(value as EHorizontalPosition);
            this.tabletHorizontalAlign.setValue(value as EHorizontalPosition);
        }

        this.horizontalAlign.setValue(value as EHorizontalPosition);
    }

    public setMobileHorizontalAlign(value: string): void {
        this.mobileHorizontalAlign.setValue(value as EHorizontalPosition);
    }

    public setTabletHorizontalAlign(value: string): void {
        this.tabletHorizontalAlign.setValue(value as EHorizontalPosition);
    }

    public setType(value: EButtonType): void {
        this.type.setValue(value);
    }

    public setButtonSize(value: ESize): void {
        this.buttonSize.setValue(value as ESize);
    }

    public dropElementItem(event: CdkDragDrop<ChatBotWidgetDataFormType['chatBots']>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        const buttons = [...this.chatBots.getRawValue()];
        moveItemInArray(buttons, event.previousIndex, event.currentIndex);

        this.chatBots.setValue(buttons as any);
    }

    public setShadow(value: boolean): void {
        this.isShadow.setValue(value);
    }

    public setShadowSettings(settings: ShadowSettings): void {
        this.shadowSettings.setValue(settings);
    }

    public setSynchronizeStyle() {
        this.mobileHorizontalAlign.setValue(this.horizontalAlign.value);
        this.tabletHorizontalAlign.setValue(this.horizontalAlign.value);

        this.isSynchronizeStyle.setValue(!this.isSynchronizeStyle.value);
    }

    public chatBots$(messenger: string): Observable<any[]> {
        return this.appFacade.isAdminCollectionWrapper$(this.chatBotsCollectionFacade.chatBots$(messenger));
    }

    public get chatBots(): ChatBotWidgetDataFormType['chatBots'] {
        return this.data.get('chatBots') as ChatBotWidgetDataFormType['chatBots'];
    }

    public get type(): ChatBotWidgetDataFormType['type'] {
        return this.data.get('type') as ChatBotWidgetDataFormType['type'];
    }

    public get horizontalAlign(): ChatBotWidgetDataFormType['horizontalAlign'] {
        return this.data.get('horizontalAlign') as ChatBotWidgetDataFormType['horizontalAlign'];
    }

    public get mobileHorizontalAlign(): ChatBotWidgetDataFormType['mobileHorizontalAlign'] {
        return this.data.get('mobileHorizontalAlign') as ChatBotWidgetDataFormType['mobileHorizontalAlign'];
    }

    public get tabletHorizontalAlign(): ChatBotWidgetDataFormType['tabletHorizontalAlign'] {
        return this.data.get('tabletHorizontalAlign') as ChatBotWidgetDataFormType['tabletHorizontalAlign'];
    }

    public get height(): ChatBotWidgetDataFormType['height'] {
        return this.data.get('height') as ChatBotWidgetDataFormType['height'];
    }

    public get styleType(): ChatBotWidgetDataFormType['styleType'] {
        return this.data.get('styleType') as ChatBotWidgetDataFormType['styleType'];
    }

    public get iconType(): ChatBotWidgetDataFormType['iconType'] {
        return this.data.get('iconType') as ChatBotWidgetDataFormType['iconType'];
    }

    public get buttonSize(): ChatBotWidgetDataFormType['buttonSize'] {
        return this.data.get('buttonSize') as ChatBotWidgetDataFormType['buttonSize'];
    }

    public get buttonBase(): ChatBotWidgetDataFormType['buttonBase'] {
        return this.data.get('buttonBase') as ChatBotWidgetDataFormType['buttonBase'];
    }

    public get buttonHover(): ChatBotWidgetDataFormType['buttonHover'] {
        return this.data.get('buttonHover') as ChatBotWidgetDataFormType['buttonHover'];
    }

    public get isIndependentCorners(): ChatBotWidgetDataFormType['isIndependentCorners'] {
        return this.data.get('isIndependentCorners') as ChatBotWidgetDataFormType['isIndependentCorners'];
    }

    public get advancedRadius(): ChatBotWidgetDataFormType['advancedRadius'] {
        return this.data.get('advancedRadius') as ChatBotWidgetDataFormType['advancedRadius'];
    }

    public get advancedBorderSettings(): ChatBotWidgetDataFormType['advancedBorderSettings'] {
        return this.data.get('advancedBorderSettings') as ChatBotWidgetDataFormType['advancedBorderSettings'];
    }

    public get isSynchronizeStyle(): ChatBotWidgetDataFormType['isSynchronizeStyle'] {
        return this.data.get('isSynchronizeStyle') as ChatBotWidgetDataFormType['isSynchronizeStyle'];
    }

    public get position(): ChatBotWidgetDataFormType['position'] {
        return this.data.get('position') as ChatBotWidgetDataFormType['position'];
    }

    public get hideOnMobile(): ChatBotWidgetDataFormType['hideOnMobile'] {
        return this.data.get('hideOnMobile') as ChatBotWidgetDataFormType['hideOnMobile'];
    }

    public get hideOnDesktop(): ChatBotWidgetDataFormType['hideOnDesktop'] {
        return this.data.get('hideOnDesktop') as ChatBotWidgetDataFormType['hideOnDesktop'];
    }

    public get hideOnTablet(): ChatBotWidgetDataFormType['hideOnTablet'] {
        return this.data.get('hideOnTablet') as ChatBotWidgetDataFormType['hideOnTablet'];
    }

    public get isShadow(): ChatBotWidgetDataFormType['isShadow'] {
        return this.data.get('isShadow') as ChatBotWidgetDataFormType['isShadow'];
    }

    public get shadowSettings(): ChatBotWidgetDataFormType['shadowSettings'] {
        return this.data.get('shadowSettings') as ChatBotWidgetDataFormType['shadowSettings'];
    }

    public get shadowSettingsValue(): ChatBotWidgetData['shadowSettings'] {
        return this.shadowSettings.value;
    }
}
